<template>
  <prime-panel
    :collapsed="!isActive"
    class="p-mb-3"
    :class="{ 'active-item': isActive, 'mobile-view': $isMobile() }"
    @click="handleClick"
    @dblclick="handleDoubleClick"
    @mouseenter="showActions = true"
    @mouseleave="showActions = false"
    @mousedown.left="preventSelectionDbl"
  >
    <template #header>
      <div v-if="fields.switchInput" class="p-mr-3">
        <tm-switch v-model="switchState" :disabled="fields.switchInput.disabled === true" @click.stop />
      </div>
      <div v-if="fields.checkbox" class="p-mr-3">
        <prime-checkbox v-model="selectedItems" :value="item" @click.stop />
      </div>
      <div v-if="['matrices', 'calibrations', 'scenarios'].includes(type)">
        <tm-state-icon
          :item="item"
          :item-type="type"
          :is-hoverable="type === 'scenarios'"
          class="p-mr-3"
          @click="$emit('icon:clicked', item)"
        />
      </div>
      <div v-if="fields.modIcon" class="p-mr-3">
        <tm-button
          type="create"
          :icon="fields.modIcon.icon"
          :border-color="fields.modIcon.border"
          class="p-mr-1 panel-icon"
        />
      </div>
      <div v-if="type === 'validations' && item.isAuto" class="p-mr-3">
        <i
          class="auto-icon"
          :class="{ 'ri-calendar-close-line': item.isStopped, 'ri-calendar-2-line': !item.isStopped }"
        />
      </div>
      <div>
        <span class="item-heading">{{ getItemName() }}</span>
      </div>
      <div class="p-ml-auto item-date" :class="{ transparent: showActions }">
        <span v-if="type === 'datasets'">{{ $t(`models.${item.type}`) }}</span>
        <span v-else-if="type === 'matrices'"> {{ item.isDefault ? $t('models.default') : '' }}</span>
        <span v-else-if="['calibrations', 'validations', 'validationResults'].includes(type)"></span>
        <span v-else>{{ $tmdate.formatScenarioDatePeriod(item[fields.dates.from], item[fields.dates.to]) }}</span>
      </div>
      <div v-show="showActions" class="p-py-3 p-pl-3 item-actions">
        <a
          v-for="action in fields.actions"
          :key="action.action"
          v-tooltip="$t(`scenarios.${action.action}`)"
          class="p-mr-3 item-action"
          ><i
            :class="action.icon + ' pi'"
            :data-type="action.action"
            @click.stop="$emit('action:clicked', action.action)"
        /></a>
      </div>
    </template>

    <div class="p-d-flex">
      <div v-for="(opt, i) in fields.content" :key="i" class="p-mr-2" :class="opt.class">
        <span v-if="opt.field">{{ $t(opt.name) }}: </span>
        <strong v-else>{{ $t(opt.name) }}</strong>
        <strong v-if="opt.field">{{ getItemFieldValue(item, opt.field) }}</strong>
        <strong v-if="opt.field2">/{{ getItemFieldValue(item, opt.field2) }}</strong>
        <span v-if="opt.units">&nbsp;{{ $t(opt.units) }}</span>
      </div>
      <div class="p-ml-auto">
        <template v-if="['scenarios', 'events'].includes(type)">
          <span>{{ $t('scenarios.last edit') }}: </span>
          <strong>{{ $d(new Date(item[fields.dates.lastEdit]), 'default') }}</strong>
        </template>
        <template v-if="['datasets', 'matrices', 'calibrations', 'validations'].includes(type)">
          <span>{{ $t(`models.${type === 'calibrations' ? 'calibration' : 'upload'} date`) }}: </span>
          <strong>{{ $d(new Date(item.createdAt), 'default') }}</strong>
        </template>
        <template v-if="['validationResults'].includes(type)">
          <span>GEH: </span>
          <strong>{{ item.geh }}</strong>
        </template>
      </div>
    </div>
  </prime-panel>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { types as modificationTypes } from '@composables/useModifications';
import PrimePanel from 'primevue/panel';
import PrimeCheckbox from 'primevue/checkbox';
import TmStateIcon from '@components/views/StateIcon.vue';
import { isCalibratingStatus } from '@composables/useStatus';
import useScenarioAccesses from '@composables/useScenarioAccesses';
import { useI18n } from 'vue-i18n';
import type { TmMatrix } from '@composables/useModelMatrices';
import type { TmCalibration } from '@composables/useModelCalibrations';
import type { TmValidationResult } from '@composables/useModelValidations';

export type ListItem = { [key: string]: any };
export type ItemAction = 'enter' | 'copy' | 'delete' | 'share';
type ItemFields = {
  header: string;
  dates: { from: keyof ListItem; to: keyof ListItem; lastEdit: keyof ListItem; createdAt: keyof ListItem };
  content: {
    name: string;
    field?: keyof ListItem;
    field2?: keyof ListItem;
    units?: string;
    class?: string;
  }[];
  actions: { action: ItemAction; icon: string }[];
  switchInput?: { field: keyof ListItem; disabled: boolean };
  modIcon?: typeof modificationTypes[0];
  checkbox?: boolean;
};

const props = withDefaults(
  defineProps<{
    type?: TmItemType; // Type representing the view to witch the item belongs
    item: ListItem; //  Item object to display
    isActive?: boolean; // Whether the item is in 'active' mode and should display details
    access?: TmScenarioAccessLevel; //  User access to this item - affects displayed action icons
    accessEventObject?: TmEvent; // Event object - only needed for type 'modifications' and used for authorization
    watchMode?: boolean; // Whether to display the item in watch mode (without any action icons)
    selected?: ListItem[]; // List of selected items
  }>(),
  {
    type: 'basic',
    isActive: false,
    access: undefined,
    accessEventObject: undefined,
    watchMode: true,
    selected: () => [],
  },
);

const emit = defineEmits<{
  clicked: [item: ListItem]; // Signals that an item has been clicked on
  checked: [checked: boolean]; // Signals that an item has been checked
  'update:selected': [items: ListItem[]]; // Updates original selected model
  'action:clicked': [action: ItemAction]; // Signals that the item action was checked
  'icon:clicked': [item: ListItem]; // Signals that the status icon was clicked
}>();

const { t } = useI18n();
const { isViewerOrGreater, isInserterOrGreater, isEditor, canInserterEditEvent } = useScenarioAccesses();

const isEnterAction = computed(() => fields.value.actions.some((action) => action.action === 'enter'));

// Prevents text selection on item with enter action on doubleclick as requested in TraMod-1080,1095
const preventSelectionDbl = (ev: MouseEvent) => {
  if (isEnterAction.value && ev.detail === 2) ev.preventDefault();
};

const handleClick = (ev: MouseEvent) => {
  if (ev.detail > 1) return; // prevent multiple emits if clicked more than once
  emit('clicked', props.item);
};

const handleDoubleClick = () => {
  if (isEnterAction.value) emit('action:clicked', 'enter');
};

const getCustomScenarioFields = (): Partial<ItemFields> => {
  const scenarioItem = props.item as TmScenario;
  return {
    actions: [
      ...(props.watchMode || isViewerOrGreater(scenarioItem.level)
        ? [{ action: 'enter' as ItemAction, icon: 'pi-directions' }]
        : []),
      ...(!props.watchMode && isEditor(scenarioItem.level) && _isItemValid(scenarioItem)
        ? [{ action: 'share' as ItemAction, icon: 'pi-share-alt' }]
        : []),
      ...(!props.watchMode && _isItemValid(scenarioItem) ? [{ action: 'copy' as ItemAction, icon: 'pi-copy' }] : []),
      ...(!props.watchMode && isEditor(scenarioItem.level) && _isItemValid(scenarioItem)
        ? [{ action: 'delete' as ItemAction, icon: 'pi-trash' }]
        : []),
    ],
  };
};

const getCustomEventFields = (): Partial<ItemFields> => {
  const eventItem = { ...props.item, level: props.access } as TmEvent & { level: TmScenarioAccessLevel };
  return {
    actions: [
      ...(props.watchMode || isViewerOrGreater(props.access)
        ? [{ action: 'enter' as ItemAction, icon: 'pi-directions' }]
        : []),
      ...(!props.watchMode && isInserterOrGreater(props.access) && _isItemValid(eventItem)
        ? [{ action: 'copy' as ItemAction, icon: 'pi-copy' }]
        : []),
      ...(!props.watchMode && (isEditor(props.access) || canInserterEditEvent(eventItem)) && _isItemValid(eventItem)
        ? [{ action: 'delete' as ItemAction, icon: 'pi-trash' }]
        : []),
    ],
    switchInput: {
      field: 'included',
      disabled: props.watchMode || !isEditor(props.access),
    },
  };
};

const getCustomModificationFields = (): Partial<ItemFields> => {
  const modificationItem = props.item as TmModification;
  const eventItem = { ...props.accessEventObject, level: props.access } as TmEvent & { level: TmScenarioAccessLevel };
  return {
    content: [
      ...(modificationItem.type == 'link' && modificationItem.mode == 'new'
        ? [
            {
              field: 'capacity',
              field2: modificationItem.twoWay ? 'twoWayCapacity' : '',
              name: 'modifications.capacity',
              units: 'map.vehicles per hour',
            },
            {
              field: 'speed',
              field2: modificationItem.twoWay ? 'twoWaySpeed' : '',
              name: 'modifications.speed',
              units: 'map.kilometers per hour',
            },
          ]
        : []),
      ...(modificationItem.type == 'link' &&
      modificationItem.mode == 'existing' &&
      (modificationItem.capacity > 0 || modificationItem.speed > 0)
        ? [
            {
              field: 'capacity',
              name: 'modifications.capacity',
              units: `map.${modificationItem.hasRelativeData ? 'percent' : 'vehicles per hour'}`,
            },
            {
              field: 'speed',
              name: 'modifications.speed',
              units: `map.${modificationItem.hasRelativeData ? 'percent' : 'kilometers per hour'}`,
            },
          ]
        : []),
      ...(modificationItem.type == 'link' &&
      modificationItem.mode == 'existing' &&
      !modificationItem.capacity &&
      !modificationItem.speed
        ? [{ name: 'modifications.closure' }]
        : []),
      ...(modificationItem.type == 'node' && modificationItem.mode == 'existing' && modificationItem.cost < 0
        ? [{ name: 'modifications.closure' }]
        : []),
      ...(modificationItem.type == 'node' && modificationItem.mode == 'existing' && modificationItem.cost > 0
        ? [
            {
              field: 'cost',
              name: 'modifications.delay',
              units: 'modifications.seconds',
            },
          ]
        : []),
      ...(modificationItem.type == 'generator'
        ? [
            {
              field: 'inTraffic',
              field2: 'outTraffic',
              name: 'modifications.target source traffic',
              units: `map.${modificationItem.hasRelativeData ? 'percent' : 'vehicles per hour'}`,
            },
          ]
        : []),
      ...(modificationItem.type == 'info' ? [{ field: 'iconName', name: 'modifications.icon' }] : []),
    ],
    actions: [
      ...(props.watchMode || isViewerOrGreater(props.access)
        ? [{ action: 'enter' as ItemAction, icon: 'pi-directions' }]
        : []),
      ...(!props.watchMode &&
      (isEditor(props.access) || canInserterEditEvent(eventItem)) &&
      _isItemValid(modificationItem)
        ? [{ action: 'copy' as ItemAction, icon: 'pi-copy' }]
        : []),
      ...(!props.watchMode &&
      (isEditor(props.access) || canInserterEditEvent(eventItem)) &&
      _isItemValid(modificationItem)
        ? [{ action: 'delete' as ItemAction, icon: 'pi-trash' }]
        : []),
    ],
    modIcon: modificationTypes.find((el) => el.type == modificationItem.type && el.mode == modificationItem.mode),
  };
};

const getCustomImportFields = (): Partial<ItemFields> => ({
  content: [
    { field: 'scenarioName', name: 'scenarios.scenario' },
    { field: 'included', name: 'scenarios.included', class: 'p-ml-auto' },
  ],
  checkbox: true,
});

const getCustomDatasetFields = (): Partial<ItemFields> => ({
  content: [{ field: 'owner', name: 'models.owner' }],
  actions: [
    { action: 'enter' as ItemAction, icon: 'pi-directions' },
    { action: 'delete' as ItemAction, icon: 'pi-trash' },
  ],
  checkbox: false,
});

const getCustomMatrixFields = (): Partial<ItemFields> => {
  const matrixItem = props.item as TmMatrix;
  const isCalibrating = isCalibratingStatus(matrixItem.status);
  return {
    content: [{ field: 'owner', name: 'models.owner' }],
    actions: [
      { action: 'enter' as ItemAction, icon: 'pi-directions' },
      ...(matrixItem.isDefault || matrixItem.isActive || isCalibrating
        ? []
        : [{ action: 'delete' as ItemAction, icon: 'pi-trash' }]),
    ],
    checkbox: false,
  };
};

const getCustomCalibrationFields = (): Partial<ItemFields> => {
  const calibrationItem = props.item as TmCalibration;
  const isCalibrating = isCalibratingStatus(calibrationItem.status);
  return {
    content: [{ field: 'owner', name: 'models.owner' }],
    actions: [
      { action: 'enter' as ItemAction, icon: 'pi-directions' },
      ...(isCalibrating ? [] : [{ action: 'delete' as ItemAction, icon: 'pi-trash' }]),
    ],
    checkbox: false,
  };
};

const getCustomValidationFields = (): Partial<ItemFields> => {
  return {
    content: [{ field: 'owner', name: 'models.owner' }],
    actions: [
      { action: 'enter' as ItemAction, icon: 'pi-directions' },
      { action: 'delete' as ItemAction, icon: 'pi-trash' },
    ],
    checkbox: false,
  };
};

const getCustomValidationResultFields = (): Partial<ItemFields> => {
  const resultItem = props.item as TmValidationResult;
  return {
    content: [{ field: 'median', name: 'models.geh median' }],
    actions: [
      { action: 'enter' as ItemAction, icon: 'pi-directions' },
      ...(resultItem.isAuto ? [{ action: 'delete' as ItemAction, icon: 'pi-trash' }] : []),
    ],
    checkbox: false,
  };
};

const getItemFieldValue = (item: ListItem, field: keyof ListItem) => {
  if (item[field] === true) return t('scenarios.yes');
  if (item[field] === false) return t('scenarios.no');
  if (item[field] === null) return t('modifications.original');
  return item[field];
};

const fields = computed<ItemFields>(() => ({
  header: 'name',
  dates: { from: 'dateFrom', to: 'dateTo', lastEdit: 'updatedAt', createdAt: 'createdAt' },
  content: [{ field: 'owner', name: 'scenarios.owner' }],
  actions: [],
  ...(props.type === 'scenarios' ? getCustomScenarioFields() : {}),
  ...(props.type === 'events' ? getCustomEventFields() : {}),
  ...(props.type === 'modifications' ? getCustomModificationFields() : {}),
  ...(props.type === 'imports' ? getCustomImportFields() : {}),
  ...(props.type === 'datasets' ? getCustomDatasetFields() : {}),
  ...(props.type === 'matrices' ? getCustomMatrixFields() : {}),
  ...(props.type === 'calibrations' ? getCustomCalibrationFields() : {}),
  ...(props.type === 'validations' ? getCustomValidationFields() : {}),
  ...(props.type === 'validationResults' ? getCustomValidationResultFields() : {}),
}));

const switchState = computed<boolean>({
  get: () => !!fields.value.switchInput && !!props.item[fields.value.switchInput.field as keyof typeof props.item],
  set: (newState) => emit('checked', newState),
});
const showActions = ref<boolean>(false);
const getItemName = (): string => {
  if (props.type !== 'modifications') return props.item[fields.value.header as keyof typeof props.item];
  // custom name for modifications
  const modificationItem = props.item as TmModification;
  return modificationItem.name || `${modificationItem.type}-${modificationItem.id}`;
};

const selectedItems = computed<ListItem[]>({
  get: () => props.selected,
  set: (selected) => emit('update:selected', selected),
});

const _isItemValid = (item: ListItem): boolean => [undefined, true].includes(item.hasModelSectionsValid);
</script>

<style scoped>
.item-actions {
  white-space: nowrap;
  position: absolute;
  right: 0;
  background-color: rgb(248, 249, 250);
}
.item-action {
  cursor: pointer;
  color: #0094d2;
}
.p-panel-header:hover .item-heading,
.item-action:hover i,
.active-item .item-heading {
  font-weight: bold;
}
.p-panel-header .item-date.transparent {
  opacity: 0.5;
}
.tm-button.panel-icon :deep(.p-button) {
  font-size: 0.8rem;
  height: 1.8rem !important;
  width: 1.8rem !important;
  pointer-events: none;
}
.mobile-view .item-date,
.mobile-view .item-actions,
.mobile-view :deep(.p-toggleable-content) {
  display: none;
}
.auto-icon {
  color: #0094d2;
  font-size: 1.5rem;
}
</style>
